import { useCallback, useMemo, useState } from "react";
import { useGroupMessageLog, useGroupEvents, useStakeholders } from "../../lib/api";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import { MessageList } from "./MessageList";
import { ActivityTimeline, EVENT_ICON, detailText, formatDay, formatTime } from "./ActivityTimeline";
import { GROUP_EVENT_LABELS, labelForEvent } from "../../lib/groupEventLabels";
import { Icon } from "../msd/Icon";
import { AN_CARD, AN_H, AN_SUB, AN_T } from "../msd/analytics";
import { Avatar, CARD, DISP, LinkBtn, Seg2, Tag, cx } from "../msd/ui";
import { FileTile, KeyIcon, KeyRow, VehiclePic, dayLabel, mediaRowsOf, servicePeriod } from "../../pages/msd/history/parts";

const TABS = [
  { key: "chat", label: "Messages" },
  { key: "media", label: "Files" },
  { key: "activity", label: "Activity" },
];

// The events worth lifting out of the full activity log, and the colour of
// each one's mark. Joins, invites and ordinary messages stay in the log only.
const KEY_EVENT_COLOR = {
  group_created: "#3A5F94",
  job_card_sent: "#3A5F94",
  document_sent: "#3A5F94",
  pre_delivery_feedback_sent: "#3A5F94",
  post_delivery_feedback_sent: "#3A5F94",
  vehicle_delivered: "#17B26A",
  sla_rung_fired: "#F79009",
  complaint_confirmation_received: "#F79009",
  complaint_registered: "#F04438",
  staff_message_blocked: "#F04438",
  wa_group_deleted: "#8E8E93",
};

// How many files the side card shows before it points at the Files tab.
const FILES_PREVIEW = 6;

const FIELD = "h-[34px] rounded-[10px] border-0 bg-msd-ground px-[10px] text-[13px] text-msd-ink outline-none focus:shadow-[0_0_0_2px_#3A5F94]";
const BTN_PLAIN =
  "inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-msd-surface px-[14px] text-[14px] font-semibold text-msd-ink no-underline shadow-msd-e1 hover:shadow-[0_0_0_.5px_rgba(16,24,40,.06),0_2px_4px_rgba(16,24,40,.08),0_6px_14px_rgba(16,24,40,.08)]";

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <span className="block text-[12px] text-msd-muted">{label}</span>
      <b className="text-[15px] font-semibold">{value}</b>
    </div>
  );
}

// .hx-head — the vehicle, who it belongs to and where it was serviced.
function HeadCard({ g, deletedAt }) {
  const { roleShort } = useTenantTerms();
  const model = [g?.["Vehicle Model"], g?.["Vehicle Variant"]].filter(Boolean).join(" ");
  const sub = [g?.["Customer Name"], model, g?.["Customer Number"]].filter(Boolean).join(" · ");
  return (
    <section className={cx(AN_CARD, "flex flex-wrap items-center gap-5 text-msd-ink")}>
      <VehiclePic group={g} hero className="h-[110px] w-[150px] rounded-2xl" />
      <div className="min-w-0 grow">
        <div className="flex flex-wrap items-center gap-[10px]">
          <h1 className={cx("m-0 break-words text-[28px] font-bold", DISP)}>{g?.["Group Name"] || "Untitled group"}</h1>
          {deletedAt ? (
            <Tag tone="grey" icon="x" title="WhatsApp group was deleted. This history is read-only.">
              Group deleted {dayLabel(deletedAt)}
            </Tag>
          ) : null}
        </div>
        <div className="mt-[2px] text-[15px] text-msd-muted">{sub || "—"}</div>
        <div className="mt-[14px] flex flex-wrap gap-7">
          <Fact label="Workshop" value={g?.["Workshop Location"]} />
          <Fact label={roleShort("sa") || "Advisor"} value={g?.["Service Advisor Name"]} />
          <Fact label="Service type" value={g?.["Service Type"]} />
          <Fact label="Service period" value={servicePeriod(g)} />
        </div>
      </div>
      <div className="flex flex-col gap-2 max-[1150px]:flex-row">
        <a href="https://app.chat360.io" target="_blank" rel="noopener noreferrer" className={BTN_PLAIN}>
          Open in Chat360 ↗
        </a>
      </div>
    </section>
  );
}

function MediaGrid({ mediaRows }) {
  if (!mediaRows.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-msd-soft text-msd-faint">
          <Icon name="img" size={18} />
        </div>
        <h4 className="m-0 text-[15px] font-semibold text-msd-ink">No media yet</h4>
        <p className="mt-1 max-w-xs text-[13px] text-msd-muted">
          Photos, videos and documents shared in this group will appear here.
        </p>
      </div>
    );
  }
  return (
    // The padding leaves room for the tiles' shadows inside the scroll box.
    <div className="grid flex-1 grid-cols-3 content-start gap-3 overflow-y-auto p-[6px] max-[640px]:grid-cols-2">
      {mediaRows.map((m) => (
        <FileTile key={m.message_id || m.id} msg={m} />
      ))}
    </div>
  );
}

const EVENT_TYPE_OPTIONS = Object.keys(GROUP_EVENT_LABELS);

/**
 * One group's read-only chat history, laid out as the prototype's record view:
 * the vehicle header, the conversation (messages, files, activity log) and,
 * beside it, the key moments, files and people. All roles, not just
 * customer/SA (that filter stays on the Desk, which is where staff send from).
 */
export function ChatHistoryPanel({ row, g }) {
  const [tab, setTab] = useState("chat");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [eventType, setEventType] = useState("");
  const { roleShort } = useTenantTerms();
  const {
    data: msgData,
    isLoading: msgLoading,
    isError: msgError,
    refetch: refetchMessages,
  } = useGroupMessageLog(row);
  const {
    data: eventsData,
    isLoading: eventsLoading,
    isError: eventsError,
    refetch: refetchEvents,
  } = useGroupEvents(row);
  const { data: stakeholdersData } = useStakeholders(g?.["Workshop Location"]);
  const stakeholders = stakeholdersData?.stakeholders || [];
  const messages = useMemo(() => msgData?.rows || [], [msgData]);
  const allEvents = useMemo(() => eventsData?.rows || [], [eventsData]);
  const mediaRows = useMemo(() => mediaRowsOf(messages), [messages]);
  const deletedAt = g?.["WA Deleted At"];

  // Client-side only — the whole group's event list is already fetched
  // once; filtering it doesn't need another request.
  const events = useMemo(() => {
    return allEvents.filter((ev) => {
      if (eventType && ev.event_type !== eventType) return false;
      const day = (ev.occurred_at || "").slice(0, 10);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [allEvents, dateFrom, dateTo, eventType]);

  const hasActiveFilters = Boolean(dateFrom || dateTo || eventType);
  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setEventType("");
  };

  // Full history (every role), not just one SA's own number like the Desk:
  // customer on the left, every staff/system role on the right.
  const isOwn = useCallback((msg) => msg.sender_role !== "customer", []);

  const counts = { chat: messages.length, media: mediaRows.length, activity: allEvents.length };

  const keyEvents = useMemo(() => allEvents.filter((ev) => KEY_EVENT_COLOR[ev.event_type]), [allEvents]);
  // A group deleted before its events were recorded still gets its closing row.
  const deletedWithoutEvent = Boolean(deletedAt) && !keyEvents.some((ev) => ev.event_type === "wa_group_deleted");

  const people = [
    g?.["Customer Name"] ? { name: g["Customer Name"], role: "Customer" } : null,
    g?.["Service Advisor Name"] ? { name: g["Service Advisor Name"], role: roleShort("sa") } : null,
    ...stakeholders.filter((s) => s.name).map((s) => ({ name: s.name, role: roleShort(s.role) || s.role })),
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-[18px]" data-testid="chat-history-panel">
      <HeadCard g={g} deletedAt={deletedAt} />

      <div className="grid grid-cols-[minmax(0,1fr)_360px] items-start gap-[18px] max-[1150px]:grid-cols-1">
        <section className={cx(CARD, "flex min-w-0 flex-col px-5 pb-[22px] pt-[18px] text-msd-ink")}>
          <div className={cx(AN_H, "!items-center")}>
            <div className={AN_T}>Conversation</div>
            <span className={AN_SUB}>Read only</span>
          </div>

          <div className="mb-1 mt-[6px]">
            <Seg2
              label="Conversation view"
              value={tab}
              onChange={setTab}
              options={TABS.map((t) => [
                t.key,
                <>
                  {t.label}
                  {counts[t.key] ? <span className="opacity-70"> {counts[t.key]}</span> : null}
                </>,
              ])}
            />
          </div>

          {tab === "activity" && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                aria-label="From date"
                data-testid="activity-filter-from"
                className={FIELD}
              />
              <span className={AN_SUB}>to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                aria-label="To date"
                data-testid="activity-filter-to"
                className={FIELD}
              />
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                aria-label="Event type"
                data-testid="activity-filter-type"
                className={FIELD}
              >
                <option value="">All event types</option>
                {EVENT_TYPE_OPTIONS.map((key) => (
                  <option key={key} value={key}>
                    {GROUP_EVENT_LABELS[key].label}
                  </option>
                ))}
              </select>
              {hasActiveFilters && (
                <LinkBtn onClick={clearFilters} className="!text-[13px]">
                  <Icon name="x" size={13} stroke={2.2} /> Clear
                </LinkBtn>
              )}
            </div>
          )}

          {/* A fixed-height box: the message list scrolls itself to the newest
              message, which must not drag the whole page down with it. */}
          <div className="relative mt-[6px] flex h-[640px] min-h-0 flex-col overflow-hidden max-[640px]:h-[520px]">
            {tab === "chat" && (
              <MessageList
                messages={messages}
                filter={null}
                group={g}
                stakeholders={stakeholders}
                isOwn={isOwn}
                isLoading={msgLoading}
                isError={msgError}
                onRetry={refetchMessages}
              />
            )}
            {tab === "media" && <MediaGrid mediaRows={mediaRows} />}
            {tab === "activity" && (
              <ActivityTimeline
                events={events}
                isLoading={eventsLoading}
                isError={eventsError}
                onRetry={refetchEvents}
              />
            )}
          </div>
        </section>

        <div className="flex min-w-0 flex-col gap-[18px] text-msd-ink">
          {(keyEvents.length > 0 || deletedWithoutEvent) && (
            <section className={AN_CARD}>
              <div className={cx(AN_T, "mb-[6px]")}>Key moments</div>
              {keyEvents.map((ev) => (
                <KeyRow
                  key={ev.id}
                  mark={<KeyIcon icon={EVENT_ICON[ev.event_type] || "info"} color={KEY_EVENT_COLOR[ev.event_type]} />}
                  title={labelForEvent(ev.event_type).label}
                  sub={[ev.actor_name, detailText(ev)].filter(Boolean).join(" · ")}
                  aside={[formatDay(ev.occurred_at), formatTime(ev.occurred_at)].filter(Boolean).join(", ")}
                />
              ))}
              {deletedWithoutEvent && (
                <KeyRow
                  mark={<KeyIcon icon="x" color="#8E8E93" />}
                  title="Group deleted"
                  sub="Kept here for records"
                  aside={dayLabel(deletedAt)}
                />
              )}
            </section>
          )}

          <section className={AN_CARD}>
            <div className={cx(AN_T, "mb-3")}>
              Files <span className={cx(AN_SUB, "font-normal")}>{mediaRows.length}</span>
            </div>
            {mediaRows.length ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {mediaRows.slice(0, FILES_PREVIEW).map((m) => (
                    <FileTile key={m.message_id || m.id} msg={m} />
                  ))}
                </div>
                {mediaRows.length > FILES_PREVIEW && (
                  <LinkBtn onClick={() => setTab("media")} className="mt-[14px]">
                    View all {mediaRows.length} files
                  </LinkBtn>
                )}
              </>
            ) : (
              <div className={AN_SUB}>{msgLoading ? "Loading…" : "No files were shared."}</div>
            )}
          </section>

          {people.length > 0 && (
            <section className={AN_CARD}>
              <div className={cx(AN_T, "mb-[6px]")}>People</div>
              {people.map((p, i) => (
                <KeyRow key={`${p.name}-${p.role}-${i}`} tight mark={<Avatar name={p.name} size={32} />} title={p.name} aside={p.role} />
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
